import {
  Body,
  Button,
  H5,
  Stack,
  useWindowProperties,
} from '@qred/components-library';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import AppShell from '~/components/shared/AppShell/AppShell';
import Navbar from '~/components/shared/Navbar/Navbar';
import Stepper from '~/components/shared/Stepper/Stepper';
import { StepperTimeline } from '~/components/shared/StepperTimeline/StepperTimeline';
import { TopFixedContainer } from '~/components/shared/TopFixedContainer/TopFixedContainer';
import { getHomePageByMarket } from '~/constants/markets';
import { StepStatus } from '~/enums';
import { areSameCompanies } from '~/helpers/utils';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { isInIframe } from '~/iframeMode';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import {
  patchCardApplication,
  postCardApplication,
} from '~/store/actions/cardApplicationActions';
import {
  goToPreviousStep,
  setCurrentStepName,
} from '~/store/slices/cardApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardPageContainer,
  StepsContainer,
} from '~/styles/BaseStyledComponents';
import { cardApplicationSteps } from './cardApplicationSteps';
export const Steps = () => {
  const dispatch = useAppDispatch();

  const { isMobile } = useWindowProperties();
  const {
    apiStatus,
    currentStepNumber,
    form,
    currentApplicationOrganizationIdentifier,
  } = useSelector((state: RootState) => state.cardApplication);
  const { market, language } = useSelector((state: RootState) => state.intl);
  const t = useTranslate();

  const currentStepConfig = cardApplicationSteps[currentStepNumber - 1];
  const isFirstStep = currentStepNumber === 1;
  const isLastStep = currentStepNumber === cardApplicationSteps.length;
  const stepIsLoading =
    apiStatus.post === ApiStatus.Started ||
    apiStatus.patch === ApiStatus.Started;

  const onBackButtonClick = () => {
    dispatch(
      trackCardApplicationActions({ actionName: 'back_button_pressed' })
    );
    if (isFirstStep) {
      window.location.assign(getHomePageByMarket(market, language));
    } else {
      dispatch(goToPreviousStep());
      dispatch(trackCardApplicationActions({ stepStatus: StepStatus.Edit }));
    }
  };

  const onSubmit = () => {
    dispatch(
      trackCardApplicationActions({ actionName: 'submit_button_pressed' })
    );
    dispatch(patchCardApplication({ finalized: true }));
  };

  const onNextStepClick = () => {
    dispatch(
      trackCardApplicationActions({ actionName: 'next_button_pressed' })
    );
    const companyHasChanged = !areSameCompanies(
      form.organizationNumber,
      currentApplicationOrganizationIdentifier
    );
    if (companyHasChanged) {
      dispatch(postCardApplication({ shouldGoToNextStep: true }));
    } else {
      dispatch(patchCardApplication({ shouldGoToNextStep: true }));
    }
  };

  useEffect(() => {
    dispatch(setCurrentStepName(currentStepConfig.name));
    dispatch(trackCardApplicationActions({ stepStatus: StepStatus.Started }));
  }, [currentStepConfig.name]);

  const backButton = isFirstStep ? undefined : (
    <Button
      variant="secondary"
      onClick={onBackButtonClick}
      fullWidth={isMobile}
      size={'md'}
    >
      {t('Back')}
    </Button>
  );

  const stepper = (
    <Stepper
      currentStepConfig={currentStepConfig}
      currentStep={currentStepNumber}
      footerCTAAction={isLastStep ? onSubmit : onNextStepClick}
      footerCTALabel={isLastStep ? 'Onboarding.Submit' : 'Onboarding.Next'}
      extraFooterContent={isInIframe ? backButton : undefined}
      extraFooterContentOptions={{ align: 'above-cta' }}
      stepIsLoading={stepIsLoading}
    />
  );

  if (isInIframe) {
    return (
      <StandardPageContainer data-testid="steps-component-iframe" $isInIframe>
        <Stack p={'24px 32px'}>
          <H5>{t('CardApplication.CardApplicationTitle')}</H5>
          <Body size="lg">
            {t('CardApplication.CardApplicationDescription')}
          </Body>
        </Stack>
        <StepperTimeline
          currentStep={currentStepNumber}
          totalSteps={cardApplicationSteps.length}
        />
        <StepsContainer $isInIframe>{stepper}</StepsContainer>
      </StandardPageContainer>
    );
  }

  return (
    <AppShell>
      <TopFixedContainer
        style={{ maxWidth: '800px', width: '100%' }}
        topFixedDisabled={!isMobile}
      >
        <Navbar
          onBackClick={onBackButtonClick}
          showBackButton
          progressStepperDots={{
            steps: cardApplicationSteps.map((step) => ({
              name: step.name,
              hideInProgressStepper: step.hideInProgressStepper,
            })),
            currentStep: currentStepNumber,
          }}
        />
      </TopFixedContainer>
      <StepsContainer>{stepper}</StepsContainer>
    </AppShell>
  );
};
